import { useEffect, useState, useMemo, useRef } from "react";
import { Coffee, SquarePlus, Calendar, MoreVertical, Trash2, Edit3, Archive, ChevronRight, MessageSquare, HelpCircle, StickyNote, Mail, Linkedin, Building } from "lucide-react";
import { auth, db } from "../firebase";
import { collection, onSnapshot, query, orderBy, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import AddCoffeeChat from "../components/addCoffeeChat";
import { useNavbar } from "./DashboardLayout";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../components/ui/dialog";


// TYPES: Define the structure of coffee chat data
type ChatStatus = "pending" | "sent" | "done";

type CoffeeChat = {
  id: string;
  name: string;
  company: string;
  role: string;
  email?: string;
  linkedin?: string;
  status: ChatStatus;
  message: string;
  questions: string[];
  notes: string;
  scheduledDate?: string;
  createdAt: any;
};

// COMPONENT: Status badge with color coding
function StatusBadge({ status }: { status: ChatStatus }) {
  const styles: Record<ChatStatus, string> = {
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    sent: "bg-blue-100 text-blue-700 border-blue-200",
    done: "bg-emerald-100 text-emerald-700 border-emerald-200",
  };

  const labels: Record<ChatStatus, string> = {
    pending: "Pending",
    sent: "Sent",
    done: "Done",
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-md border ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

// COMPONENT: Individual coffee chat card
function CoffeeChatCard({ 
  chat, 
  onDelete, 
  onEdit,
  onViewDetails
}: { 
  chat: CoffeeChat;
  onDelete: () => void;
  onEdit: () => void;
  onViewDetails: () => void;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div 
      className="rounded-2xl border border-cocoa/10 bg-cream p-5 shadow-sm hover:shadow-md transition cursor-pointer"
      onClick={onViewDetails}
    >
      {/* HEADER: Name and menu */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-[16px] font-semibold text-espresso mb-1">{chat.name}</h3>
          <div className="flex items-center gap-2 text-sm text-cocoa mb-2">
            <Building size={14} className="shrink-0" />
            <span className="truncate">{chat.role} at {chat.company}</span>
          </div>
        </div>

        {/* THREE-DOT MENU */}
        <div className="relative" ref={menuRef} onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded-md text-cocoa hover:bg-oat"
          >
            <MoreVertical size={16} />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-cocoa/10 py-1 z-10">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-espresso hover:bg-cocoa/5"
              >
                <Edit3 size={14} />
                Edit
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CONTACT INFO */}
      <div className="space-y-1.5 mb-3">
        {chat.email && (
          <div className="flex items-center gap-2 text-xs text-cocoa">
            <Mail size={12} className="shrink-0" />
            <span className="truncate">{chat.email}</span>
          </div>
        )}
        {chat.linkedin && (
          <div className="flex items-center gap-2 text-xs text-cocoa">
            <Linkedin size={12} className="shrink-0" />
            <a 
              href={chat.linkedin.startsWith('http') ? chat.linkedin : `https://${chat.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate hover:text-[#d97857]"
              onClick={(e) => e.stopPropagation()}
            >
              LinkedIn 
            </a>
          </div>
        )}
      </div>

      {/* QUICK STATS */}
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-cocoa/5">
        <div className="flex items-center gap-1.5 text-xs text-cocoa">
          <MessageSquare size={12} />
          <span>Message {chat.message ? '✓' : '✗'}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-cocoa">
          <HelpCircle size={12} />
          <span>{chat.questions.length} questions</span>
        </div>
        {chat.notes && (
          <div className="flex items-center gap-1.5 text-xs text-cocoa">
            <StickyNote size={12} />
            <span>Notes</span>
          </div>
        )}
      </div>

      {/* STATUS AND DATE */}
      <div className="flex items-center justify-between">
        <StatusBadge status={chat.status} />
        {chat.scheduledDate && (
          <div className="flex items-center gap-1.5 text-xs text-cocoa">
            <Calendar size={12} />
            <span>{new Date(chat.scheduledDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// COMPONENT: Detailed view modal
function ChatDetailsModal({ 
  chat, 
  onClose,
  onEdit 
}: { 
  chat: CoffeeChat;
  onClose: () => void;
  onEdit: () => void;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showClose={false} className="flex max-w-3xl flex-col gap-0 overflow-hidden p-0 shadow-2xl">
        {/* HEADER */}
        <div className="border-b border-cocoa/10 px-6 py-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <DialogTitle className="text-xl">{chat.name}</DialogTitle>
              <DialogDescription className="mt-1">{chat.role} at {chat.company}</DialogDescription>
              <div className="flex items-center gap-3 mt-2">
                <StatusBadge status={chat.status} />
                {chat.scheduledDate && (
                  <div className="flex items-center gap-1.5 text-xs text-cocoa">
                    <Calendar size={12} />
                    <span>Scheduled: {new Date(chat.scheduledDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-espresso hover:bg-cocoa/5"
            >
              ✕
            </button>
          </div>
        </div>

        {/* BODY - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* CONTACT INFO */}
          <section>
            <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3">
              Contact Information
            </h3>
            <div className="space-y-2 bg-cream rounded-lg p-4">
              {chat.email && (
                <div className="flex items-center gap-2 text-sm text-cocoa">
                  <Mail size={14} className="shrink-0" />
                  <span>{chat.email}</span>
                </div>
              )}
              {chat.linkedin && (
                <div className="flex items-center gap-2 text-sm text-cocoa">
                  <Linkedin size={14} className="shrink-0" />
                  <a 
                    href={chat.linkedin.startsWith('http') ? chat.linkedin : `https://${chat.linkedin}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-caramel underline"
                  >
                     LinkedIn 
                  </a>
                </div>
              )}
            </div>
          </section>

          {/* MESSAGE */}
          <section>
            <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3 flex items-center gap-2">
              <MessageSquare size={14} />
              Draft Message
            </h3>
            {chat.message ? (
              <div className="bg-cream rounded-lg p-4 border border-cocoa/10">
                <p className="text-sm text-espresso whitespace-pre-wrap leading-relaxed">
                  {chat.message}
                </p>
              </div>
            ) : (
              <div className="bg-cream rounded-lg p-4 border-2 border-dashed border-sand text-center">
                <p className="text-sm text-cocoa italic">No message drafted yet</p>
              </div>
            )}
          </section>

          {/* QUESTIONS */}
          <section>
            <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3 flex items-center gap-2">
              <HelpCircle size={14} />
              Questions to Ask ({chat.questions.length})
            </h3>
            {chat.questions.length > 0 ? (
              <ul className="space-y-2">
                {chat.questions.map((question, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 p-3 bg-cream rounded-lg border border-cocoa/10"
                  >
                    <span className="text-sm font-semibold text-cocoa shrink-0">
                      {index + 1}.
                    </span>
                    <span className="text-sm text-espresso">{question}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="bg-cream rounded-lg p-4 border-2 border-dashed border-sand text-center">
                <p className="text-sm text-cocoa italic">No questions added yet</p>
              </div>
            )}
          </section>

          {/* NOTES */}
          <section>
            <h3 className="text-sm font-semibold text-espresso uppercase tracking-wide mb-3 flex items-center gap-2">
              <StickyNote size={14} />
              Notes
            </h3>
            {chat.notes ? (
              <div className="bg-cream rounded-lg p-4 border border-cocoa/10">
                <p className="text-sm text-espresso whitespace-pre-wrap leading-relaxed">
                  {chat.notes}
                </p>
              </div>
            ) : (
              <div className="bg-cream rounded-lg p-4 border-2 border-dashed border-sand text-center">
                <p className="text-sm text-cocoa italic">No notes yet</p>
              </div>
            )}
          </section>
        </div>

        {/* FOOTER */}
        <div className="border-t border-cocoa/10 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-lg border border-cocoa/50 text-espresso hover:bg-cocoa/5 transition"
          >
            Close
          </button>
          <button
            onClick={() => {
              onEdit();
              onClose();
            }}
            className="px-6 py-2.5 rounded-lg bg-caramel text-white hover:opacity-90 transition flex items-center gap-2"
          >
            <Edit3 size={16} />
            Edit
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// MAIN COMPONENT: Coffee Chats page
export default function CoffeeChats() {
  // STATE: Store all coffee chats
  const [chats, setChats] = useState<CoffeeChat[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingChat, setEditingChat] = useState<CoffeeChat | null>(null);
  const [selectedChat, setSelectedChat] = useState<CoffeeChat | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [user, setUser] = useState(auth.currentUser);
  const { setNavbarActions } = useNavbar();


  // EFFECT 1: Listen for authentication changes
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setLoading(false);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  // EFFECT 2: Set up Firestore real-time listener
  useEffect(() => {
    if (!user) {
      setChats([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // CREATE QUERY: Get all coffee chats for this user
    const q = query(
      collection(db, "users", user.uid, "coffeeChats"),
      orderBy("createdAt", "desc")
    );

    // REAL-TIME LISTENER: Updates automatically when data changes
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatsData: CoffeeChat[] = [];
      snapshot.forEach((doc) => {
        chatsData.push({ id: doc.id, ...doc.data() } as CoffeeChat);
      });
      setChats(chatsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);


    useEffect(() => {
    setNavbarActions(
      <button
        onClick={() => {
            setEditingChat(null);
            setShowAddModal(true);
          }}
        className="inline-flex items-center gap-2 rounded-lg bg-caramel px-3 py-1 text-white hover:opacity-90 transition"
      >
        <SquarePlus size={16} />
        Add Chat
      </button>
    );

    return () => setNavbarActions(null);
}, []);

  // FUNCTION: Delete a coffee chat
  const handleDeleteChat = async (chatId: string) => {
    const user = auth.currentUser;
    if (!user) return;


    try {
      await deleteDoc(doc(db, "users", user.uid, "coffeeChats", chatId));
    } catch (error) {
      console.error("Error deleting chat:", error);
      alert("Failed to delete chat");
    }
  };

  // COMPUTED: Group chats by status
  const groupedChats = useMemo(() => {
    const pending = chats.filter(c => c.status === "pending");
    const sent = chats.filter(c => c.status === "sent");
    const done = chats.filter(c => c.status === "done");
    return { pending, sent, done };
  }, [chats]);

  return (
    <div className="w-full">
      {/* PAGE HEADER */}
    
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-cocoa">Loading coffee chats...</div>
        </div>
      ) : chats.length === 0 ? (
        <div className="text-center py-12 bg-cream rounded-2xl border-2 border-dashed border-sand">
          <Coffee size={48} className="mx-auto text-caramel mb-4" />
          <p className="text-cocoa mb-4">No coffee chats yet. Start networking!</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 text-caramel hover:underline"
          >
            <SquarePlus size={16} />
            Schedule your first coffee chat
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* PENDING SECTION */}
          {groupedChats.pending.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
                <h2 className="text-sm font-semibold text-espresso tracking-wide"> 
                  Pending <span className="text-cocoa font-normal">({groupedChats.pending.length})</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedChats.pending.map((chat) => (
                  <CoffeeChatCard
                    key={chat.id}
                    chat={chat}
                    onDelete={() => handleDeleteChat(chat.id)}
                    onEdit={() => {
                      setEditingChat(chat);
                      setShowAddModal(true);
                    }}
                    onViewDetails={() => setSelectedChat(chat)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* SENT SECTION */}
          {groupedChats.sent.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-500" />
                <h2 className="text-sm font-semibold text-espresso tracking-wide">
                  Sent <span className="text-cocoa font-normal">({groupedChats.sent.length})</span>
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {groupedChats.sent.map((chat) => (
                  <CoffeeChatCard
                    key={chat.id}
                    chat={chat}
                    onDelete={() => handleDeleteChat(chat.id)}
                    onEdit={() => {
                      setEditingChat(chat);
                      setShowAddModal(true);
                    }}
                    onViewDetails={() => setSelectedChat(chat)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* ARCHIVED (DONE) SECTION - Collapsible */}
          {groupedChats.done.length > 0 && (
            <section>
              <button
                onClick={() => setShowArchived(!showArchived)}
                className="flex items-center gap-2 mb-4 text-cocoa hover:text-espresso transition"
              >
                <Archive size={16} />
                <h2 className="text-sm font-semibold tracking-wide">
                  Completed ({groupedChats.done.length})
                </h2>
                <ChevronRight size={16} className={`transition-transform ${showArchived ? 'rotate-90' : ''}`} />
              </button>
              {showArchived && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-70">
                  {groupedChats.done.map((chat) => (
                    <CoffeeChatCard
                      key={chat.id}
                      chat={chat}
                      onDelete={() => handleDeleteChat(chat.id)}
                      onEdit={() => {
                        setEditingChat(chat);
                        setShowAddModal(true);
                      }}
                      onViewDetails={() => setSelectedChat(chat)}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      <AddCoffeeChat
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingChat(null);
        }}
        editChat={editingChat || undefined}
      />

      {/* DETAILS MODAL */}
      {selectedChat && (
        <ChatDetailsModal
          chat={selectedChat}
          onClose={() => setSelectedChat(null)}
          onEdit={() => {
            setEditingChat(selectedChat);
            setShowAddModal(true);
          }}
        />
      )}
    </div>
  );
}